import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { onSessionSignal } from '@/api/sessionSignals';
import { applyStreamEvent } from './apply';
import { recordStreamGap } from './arrivals';
import { createStreamConnection, type StreamStatus } from './connection';

/**
 * Mounts the one `EventSource` this tab gets (ADR 0002) and feeds every frame
 * into the cache.
 *
 * Mounted once, high in the tree, for the whole tab — not per view. The stream
 * is per *session*, covers every account, and the server caps a session at
 * eight of them; opening one per route would burn that cap on navigation.
 *
 * The connection is torn down on sign-out and rebuilt on a successful re-auth,
 * because a stream authorised by a session that no longer exists is exactly
 * what the server's own 15-second re-check is there to close.
 */
export function useLiveUpdates(signedIn: boolean): StreamStatus {
  const api = useApi();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>('idle');
  const statusRef = useRef<StreamStatus>('idle');

  useEffect(() => {
    if (!signedIn) return;
    // `EventSource` is absent in jsdom and in a non-browser build; without it
    // the app simply has no live updates, which is the pre-ADR-0002 behaviour
    // and not an error.
    if (typeof EventSource === 'undefined') return;

    /**
     * Was this stream ever delivering? Only then is an interruption a *gap*.
     *
     * The very first connect is not: the views mounting alongside it are
     * fetching their data over REST anyway, so announcing a gap there would
     * refetch everything twice on every sign-in.
     */
    let everOpen = false;
    let interrupted = false;

    const connection = createStreamConnection({
      // Minted per open attempt and never held (ADR 0041). Going through the
      // injected `MailApi` keeps the mock adapter working and keeps the proof
      // on the mint where it belongs.
      mintTicket: () => api.events.ticket(),
      onEvent: (event) => applyStreamEvent(queryClient, event),
      onStatus: (next) => {
        /*
         * A stream that was delivering, stopped, and came back cannot say what
         * it missed. Its resume point only exists if a real event had already
         * arrived (`hello` carries no SSE `id:`), and even a good resume point
         * older than the server's ten-minute ring gets a `resync_required`
         * instead. A reopen with no resume point gets neither — the server
         * treats it as a fresh stream and says nothing — so the client records
         * the gap and lets the views decide how to make it good.
         */
        if (next === 'open') {
          if (interrupted) recordStreamGap();
          everOpen = true;
          interrupted = false;
        } else if (everOpen && (next === 'reconnecting' || next === 'connecting')) {
          interrupted = true;
        }
        if (statusRef.current === next) return;
        statusRef.current = next;
        setStatus(next);
      },
    });
    connection.start();

    /*
     * The three moments a dead stream has to be noticed.
     *
     * `refetchOnWindowFocus` is off on purpose (see `queryClient.ts`) — focus
     * must never re-sort a list under someone mid-triage — so focus cannot be
     * the freshness mechanism. What it *can* do is make sure the freshness
     * mechanism is actually alive, which is all `wake` does: it reconnects a
     * stream that is down or silent, and the gap that reconnect records is
     * what the views then make good, quietly or behind the pill.
     */
    const wake = (): void => connection.wake();
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') wake();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    window.addEventListener('pageshow', wake);

    // A re-auth that succeeded means the cookies are good again; the stream was
    // stopped on the 401 and has to be told it may come back.
    const unsubscribe = onSessionSignal((signal) => {
      if (signal.type === 'available') connection.start();
    });

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', wake);
      window.removeEventListener('online', wake);
      window.removeEventListener('pageshow', wake);
      unsubscribe();
      connection.stop();
    };
  }, [api, queryClient, signedIn]);

  return status;
}
